import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUsers, FiArrowRight } from 'react-icons/fi';
import { PersonaAvatar } from './PersonaAvatar';
import { PulseLoader } from 'react-spinners';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

interface Persona {
  id: string;
  name: string;
  primarySpecialization?: string;
  tags?: string[];
  capabilities?: Record<string, any>;
  avatarUrl?: string;
}

interface PersonaCarouselProps {
  personas?: Persona[];
  isLoading?: boolean;
  darkMode: boolean;
  inView?: boolean;
  title?: string;
  subtitle?: string;
  staticData?: boolean;
}

export const PersonaCarousel: React.FC<PersonaCarouselProps> = ({
  personas = [],
  isLoading = false,
  darkMode,
  inView = true,
  title = "Meet Your Specialized AI Collaborators",
  subtitle = "Discover personas with expertise in different domains, ready to collaborate on your projects",
  staticData = false
}) => {
  // Auto-scrolling carousel effect
  useEffect(() => {
    if (!inView) return;
    
    const carousel = document.getElementById('persona-carousel');
    if (!carousel) return;
    
    let scrollInterval: number;
    let isPaused = false;
    
    // Start auto-scrolling after a delay
    const startAutoScroll = () => {
      scrollInterval = window.setInterval(() => {
        if (!isPaused && carousel) {
          // Get current scroll position
          const currentScroll = carousel.scrollLeft;
          const maxScroll = carousel.scrollWidth - carousel.clientWidth;
          
          // If we're at the end, go back to start
          if (currentScroll >= maxScroll - 10) {
            carousel.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            // Otherwise scroll by one card width
            carousel.scrollBy({ left: 280, behavior: 'smooth' });
          }
        }
      }, 5000); // Scroll every 5 seconds
    };
    
    // Start auto-scrolling after initial delay
    const initialDelay = setTimeout(startAutoScroll, 2000);
    
    // Pause scrolling when user interacts with carousel
    const pauseAutoScroll = () => { isPaused = true; };
    const resumeAutoScroll = () => { 
      isPaused = false;
      // Reset the interval to prevent immediate scroll after user interaction
      clearInterval(scrollInterval);
      startAutoScroll();
    };
    
    carousel.addEventListener('mouseenter', pauseAutoScroll);
    carousel.addEventListener('mouseleave', resumeAutoScroll);
    carousel.addEventListener('touchstart', pauseAutoScroll);
    carousel.addEventListener('touchend', resumeAutoScroll);
    
    // Clean up
    return () => {
      clearTimeout(initialDelay);
      clearInterval(scrollInterval);
      if (carousel) {
        carousel.removeEventListener('mouseenter', pauseAutoScroll);
        carousel.removeEventListener('mouseleave', resumeAutoScroll);
        carousel.removeEventListener('touchstart', pauseAutoScroll);
        carousel.removeEventListener('touchend', resumeAutoScroll);
      }
    };
  }, [inView]);

  return (
    <div className={`py-16 px-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`text-3xl font-black tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
            Meet Your <span className="text-blue-500">Specialized AI</span> Collaborators
          </h2>
          <p className={`max-w-2xl mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {subtitle}
          </p>
        </motion.div>
        
        {/* Scrollable horizontal container with more top padding */}
        <div className="relative pt-10">
          {/* Left navigation arrow */}
          <button 
            onClick={() => document.getElementById('persona-carousel')?.scrollBy({left: -300, behavior: 'smooth'})}
            className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-20 p-2 rounded-full ${
              darkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-800 hover:bg-gray-100'
            } shadow-lg focus:outline-none`}
            aria-label="Previous personas"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* Right navigation arrow */}
          <button 
            onClick={() => document.getElementById('persona-carousel')?.scrollBy({left: 300, behavior: 'smooth'})}
            className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-20 p-2 rounded-full ${
              darkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-800 hover:bg-gray-100'
            } shadow-lg focus:outline-none`}
            aria-label="Next personas"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Left shadow gradient for scroll indication */}
          <div className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none bg-gradient-to-r from-gray-800 to-transparent opacity-0 md:opacity-30"></div>
          
          {/* Right shadow gradient for scroll indication */}
          <div className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none bg-gradient-to-l from-gray-800 to-transparent opacity-0 md:opacity-30"></div>
          
          <motion.div
            id="persona-carousel"
            className="flex overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {isLoading ? (
              <div className="w-full flex justify-center py-20">
                <PulseLoader color="#3B82F6" size={15} margin={5} />
              </div>
            ) : personas.length > 0 ? (
              <>
                {personas.map((persona, index) => (
                  <motion.div
                    key={persona.id || index}
                    variants={itemVariants}
                    whileHover={{
                      y: -10,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                    className="relative group flex-shrink-0 w-[280px] mx-2 snap-start mt-4"
                  >
                    {/* The "Glow" Layer: Behind the card */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-0 group-hover:opacity-20 transition duration-500"></div>

                    <div className={`relative block p-5 rounded-xl border backdrop-blur-md transition-all duration-300 h-full ${
                        darkMode
                          ? 'bg-[#0f172a]/80 border-white/10 hover:border-blue-500/50 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]'
                          : 'bg-white/90 border-gray-200 hover:border-blue-500/50 shadow-xl'
                      }`}>
                      {/* Card content - now wrapped in a Link to persona details */}
                      <Link to={staticData ? "#" : `/personas/${persona.id}`} className="block mb-4">
                        <div className="flex flex-col items-center text-center">
                          {/* Enhanced Avatar Container */}
                          <div className="relative p-1 rounded-full bg-gradient-to-b from-blue-400 to-transparent mb-4">
                            <div className={`rounded-full p-1 ${darkMode ? 'bg-[#0f172a]' : 'bg-white'}`}>
                              <PersonaAvatar
                                name={persona.name}
                                specialization={persona.primarySpecialization || persona.tags?.[0]}
                                size="medium"
                                avatarUrl={persona.avatarUrl}
                              />
                            </div>
                          </div>

                          <h3 className={`font-bold text-lg tracking-tight mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {persona.name.replace(/_/g, ' ')}
                          </h3>
                          
                          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-3">
                            {persona.primarySpecialization || persona.tags?.[0] || 'AI Persona'}
                          </p>

                          {/* Optimized Tag Grid */}
                          <div className="flex flex-wrap justify-center gap-1 mb-3">
                            {persona.tags?.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-tighter rounded-md border ${
                                darkMode 
                                  ? 'bg-blue-500/5 border-blue-500/20 text-blue-300' 
                                  : 'bg-gray-100 border-gray-200 text-gray-600'
                              }`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Link>

                      {/* Footer with improved spacing and depth */}
                      <div className={`mt-auto w-full pt-3 border-t ${darkMode ? 'border-white/5' : 'border-gray-100'}`}>
                        <div className="flex justify-between items-center">
                          <div className={`text-[10px] font-medium px-1.5 py-0.5 rounded bg-white/5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {Object.keys(persona.capabilities || {}).length || 'Core'} Capabilities
                          </div>
                          <Link to={staticData ? "#" : `/chat/persona/${persona.id}`} className="flex items-center gap-1 group/btn">
                            <span className="text-xs font-bold text-blue-400 group-hover/btn:text-blue-300 transition-colors">
                              Chat
                            </span>
                            <svg className="w-3 h-3 text-blue-400 transform group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* View All Card */}
                {!staticData && (
                  <motion.div
                    variants={itemVariants}
                    className="relative flex-shrink-0 w-[200px] mx-2 snap-start flex items-center justify-center"
                  >
                    <Link 
                      to="/personas" 
                      className={`flex flex-col items-center justify-center p-6 h-full rounded-xl border-2 border-dashed ${
                        darkMode 
                          ? 'border-gray-700 hover:border-blue-500 text-gray-400 hover:text-blue-400' 
                          : 'border-gray-300 hover:border-blue-500 text-gray-500 hover:text-blue-600'
                      } transition-all duration-300`}
                    >
                      <FiUsers className="text-3xl mb-3" />
                      <span className="font-medium">View All Personas</span>
                      <FiArrowRight className="mt-2" />
                    </Link>
                  </motion.div>
                )}
              </>
            ) : (
              // Skeleton loaders - now horizontal
              Array(4).fill(0).map((_, i) => (
                <div 
                  key={i}
                  className={`flex-shrink-0 w-[280px] mx-2 p-5 rounded-xl border shadow-xl ${
                    darkMode ? 'bg-gray-900/80 border-gray-700/50' : 'bg-white/90 border-gray-200'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <div className="relative p-1 rounded-full bg-gradient-to-b from-blue-400/30 to-transparent mb-4">
                      <div className={`w-16 h-16 rounded-full ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                    </div>
                    <div className={`h-5 w-32 mb-1 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                    <div className={`h-3 w-24 mb-3 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                    <div className="mb-3 flex gap-1">
                      <div className={`h-4 w-12 rounded-md ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                      <div className={`h-4 w-12 rounded-md ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                    </div>
                    <div className={`w-full pt-3 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-center">
                        <div className={`h-4 w-16 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                        <div className={`h-4 w-12 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} animate-pulse`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};
